import { agreementApi, calendarEntriesApi, employeePaymentsApi, employeesApi } from 'api';
import { AgreementEntity, CalendarEntryEntityTypeEnum, CustomCalendarEntryTypesEntity, PaymentEntity, RewardEntity } from 'api/generated';
import { addDays, endOfMonth, format, isAfter, isBefore, isSameDay, isWithinInterval, parse, parseISO, startOfMonth } from 'date-fns';
import { decryptData, getDecryptedSpecialDailyNet } from 'utils/encryptionDecryptionAgreements';


function millisecondsToDays(milliseconds: number) {
  return Math.round(milliseconds / 1000 / 3600 / 24);
}

// A payment made after a contract ends can still be a legitimate final payroll
// for that contract (e.g. last month's salary paid the following month), so we
// keep a grace window past the contract end date before excluding a payment.
const POST_CONTRACT_PAYMENT_GRACE_DAYS = 45;

/**
 * Restrict payments to those that actually belong to an employee's contract
 * window ([contractStart, contractEnd + grace]).
 *
 * Payments are stored with the season that was selected in the dropdown at the
 * time they were created/imported, NOT the season their date falls into. So a
 * prior-season salary imported while the current season is selected gets tagged
 * to the current season and would otherwise leak into this report. Bounding by
 * the contract dates drops those mis-tagged payments from the totals and table.
 */
export function filterPaymentsToContractWindow<T extends { paymentDate: string }>(
  payments: T[],
  contractStart?: Date | null,
  contractEnd?: Date | null,
): T[] {
  if (!contractStart) return payments;
  const start = new Date(contractStart);
  start.setHours(0, 0, 0, 0);
  const end = contractEnd ? addDays(new Date(contractEnd), POST_CONTRACT_PAYMENT_GRACE_DAYS) : null;
  if (end) end.setHours(23, 59, 59, 999);
  return payments.filter((payment) => {
    const paymentDate = new Date(payment.paymentDate);
    if (paymentDate < start) return false;
    if (end && paymentDate > end) return false;
    return true;
  });
}


enum CalendarEntryType {
  HALF_DAY = 'HALF_DAY',
  DAY_OFF = 'DAY_OFF',
  VACATION = 'VACATION',
  ILL = 'ILL',
  UNPAID_LEAVE = 'UNPAID_LEAVE',
  WORK = 'WORK'
}
interface PaymentDetail {
  date: Date;
  payed: number;
  paymentId: number;
  receiptPath: string | null;
  remainingCredit: number;
  toBePayed: number;
}

interface PaymentDetail {
  date: Date;
  payed: number;
  paymentId: number;
  receiptPath: string | null;
  remainingCredit: number;
  toBePayed: number;
}


export async function getEmployeeReport(employeeId: number, seasonId: number | null, encryptionKey: string, setHasEncryptedAgreements: (arg: boolean) => void, setIsUnlimited: (arg: boolean) => void, year?: number, selectedDate?: Date) {


  const employeeToSeason = (await employeesApi.findOne(employeeId, {
    params: {
      seasonId,
      includePayments: true,
      includeContract: true,
    },

  })).data.employeesToSeasons?.at(0);

  if (!employeeToSeason)
    return;

  if (!employeeToSeason.contract)
    return;
  if (!employeeToSeason.contract?.startedOn)
    return;

  const rewards = employeeToSeason.contract.rewards ?? [];

  const contractStartDate = new Date(employeeToSeason.contract?.startedOn);
  const firstDayOfYear = year && new Date(Date.UTC(year, 0, 1));
  const startDate = firstDayOfYear ? contractStartDate > firstDayOfYear ? contractStartDate : firstDayOfYear : contractStartDate;
  let endDate = employeeToSeason.contract?.endedOn ? new Date(employeeToSeason.contract?.endedOn) : new Date(Date.UTC(startDate.getFullYear(), 11, 31));
  let isUnlimited = false;
  if (!employeeToSeason.contract?.endedOn) {
    seasonId = null;
    setIsUnlimited(true);
    isUnlimited = true;
  } else {
    setIsUnlimited(false);
    isUnlimited = false;
  }

  const agreements = ((employeeToSeason.contract as any).agreements as AgreementEntity[]).filter(a => {
    if (!isUnlimited) {
      return true;
    }
    return (isBefore(new Date(a.startedOn), endDate) || isSameDay(new Date(a.startedOn), endDate))
      && (!a.endedOn || isAfter(new Date(a.endedOn), startDate) || isSameDay(new Date(a.endedOn), startDate))
  }) as any;
  // seasonId = 0 means null in this endpoint implementation
  let payments: PaymentEntity[] = (await employeePaymentsApi.findAll(employeeId, seasonId ?? 0)).data;
  // For seasonal employees, drop payments that don't fall within this contract's
  // window — they were mis-tagged to this season at import time (see helper doc).
  if (seasonId) payments = filterPaymentsToContractWindow(payments, contractStartDate, endDate);
  const customTypes = (await calendarEntriesApi.getAllCustomTypes()).data;
  const paymentsData: Array<{
    date: Date;
    toBePayed: number,
    payed: number,
    remainingCredit: number,
    paymentId: number,
    receiptPath?: string,
    isEncrypted: boolean,
    paymentStaticType: object
  }> = [];

  let eventCount: Record<string, number> = {};

  let daysOff: number = 0;

  let workDays: number = 0;

  let total = 0;
  let totalWeightedNetDaily = 0;
  let totalDaysOff = 0;
  let todayDaysOff = 0;
  let totalDuration = 0;
  let monthly = 0;
  let avgNetDaily = 0;
  const daysOffBreakdown: Record<string, number> = {};


  const monthlyPayments: number[] = [];
  const monthlyDues: number[] = [];
  const credit: number[] = [];

  const seniority = (await employeesApi.getStats(employeeId)).data;


  if (agreements.length > 0) {
    // maxDate and minDate used to compute the monthly payments from total
    let minDate = agreements[0].startedOn;
    let maxDate = agreements[0].endedOn;

    console.log('🚀 STARTING AGREEMENTS PROCESSING:', {
      agreementsCount: agreements.length,
      selectedDate: (selectedDate ?? new Date()).toISOString(),
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      initialTodayDaysOff: todayDaysOff,
      initialTotalDaysOff: totalDaysOff
    });

    setHasEncryptedAgreements(false);
    for (const agreement of agreements as AgreementEntity[]) {
      /* if (!agreement.endedOn)
           continue;
       */

      let countStartDate = new Date(agreement.startedOn);
      const countEndDate = agreement.endedOn ? new Date(agreement.endedOn) : endDate;

      // if agreement has not and end start date should be decided by year selected
      if (!agreement.endedOn) {
        countStartDate = new Date(agreement.startedOn) > startDate ? new Date(agreement.startedOn) : startDate;
      }

      // if startDate is greater thant endDate means agreement is not valid in year selected
      if (countStartDate > countEndDate)
        continue;

      const duration = millisecondsToDays(+countEndDate - +countStartDate) + 1;
      const fromStart = millisecondsToDays(+(selectedDate ?? new Date()) - +countStartDate) + 1;

      // DEBUG: Check for potential calculation issues
      if (fromStart < 0) {
        console.warn('⚠️ NEGATIVE fromStart detected:', {
          agreementId: agreement.id,
          selectedDate: (selectedDate ?? new Date()).toISOString(),
          countStartDate: countStartDate.toISOString(),
          fromStart,
          duration
        });
      }

      if (duration < 0) {
        console.warn('⚠️ NEGATIVE duration detected:', {
          agreementId: agreement.id,
          countStartDate: countStartDate.toISOString(),
          countEndDate: countEndDate.toISOString(),
          duration
        });
      }

      // DEBUG: Log agreement details
      console.log('🔍 AGREEMENT DEBUG:', {
        agreementId: agreement.id,
        startedOn: agreement.startedOn,
        endedOn: agreement.endedOn,
        countStartDate: countStartDate.toISOString(),
        countEndDate: countEndDate.toISOString(),
        selectedDate: (selectedDate ?? new Date()).toISOString(),
        duration,
        fromStart,
        createDaysAutomatically: agreement.createDaysAutomatically,
        daysOff: agreement.daysOff,
        freeDays: agreement.freeDays,
        freeDaysLength: agreement.freeDays?.length || 0
      });

      const agreementDaysOff = agreement.createDaysAutomatically ?
        agreement.freeDays.filter((elem, index, self) => {
          return index === self.indexOf(elem);
        }).length : agreement.daysOff;

      // FIX: Handle negative fromStart properly
      let timeRatio = fromStart > duration ? duration : fromStart;

      // If the agreement hasn't started yet (fromStart < 0), it should contribute 0 to todayDaysOff
      if (fromStart < 0) {
        timeRatio = 0;
        console.log('🔧 FIXING NEGATIVE fromStart:', {
          agreementId: agreement.id,
          fromStart,
          timeRatio: 0,
          reason: 'Agreement has not started yet',
          selectedDate: (selectedDate ?? new Date()).toISOString(),
          agreementStart: agreement.startedOn
        });
      }

      // Additional safety check: ensure timeRatio is never negative
      if (timeRatio < 0) {
        console.warn('🚨 NEGATIVE timeRatio detected, setting to 0:', {
          agreementId: agreement.id,
          timeRatio,
          fromStart,
          duration
        });
        timeRatio = 0;
      }

      const todayDaysOffContribution = agreementDaysOff * timeRatio / 7;
      const totalDaysOffContribution = agreementDaysOff * duration / 7;

      console.log('🔍 DAYS OFF CALCULATION:', {
        agreementId: agreement.id,
        agreementDaysOff,
        timeRatio,
        todayDaysOffContribution,
        totalDaysOffContribution,
        beforeTodayDaysOff: todayDaysOff,
        beforeTotalDaysOff: totalDaysOff
      });

      todayDaysOff = todayDaysOff + todayDaysOffContribution;
      totalDaysOff = totalDaysOff + totalDaysOffContribution;

      console.log('🔍 ACCUMULATED VALUES:', {
        agreementId: agreement.id,
        afterTodayDaysOff: todayDaysOff,
        afterTotalDaysOff: totalDaysOff
      });


      let netValue = 0;
      if (agreement.net_daily_encrypted === null && agreement.net_daily) {
        netValue = agreement.net_daily;
      } else if (agreement.net_daily_encrypted !== null) {
        const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
        if (net !== '') {
          netValue = Number(net);
        } else {
          setHasEncryptedAgreements(true);
          continue;
        }

      }

      totalDuration += duration;

      totalWeightedNetDaily += netValue * duration;

      if (minDate > countStartDate)
        minDate = countStartDate;

      if (maxDate < countEndDate)
        maxDate = countEndDate;

      total = total + netValue * duration;


    }

    console.log('🏁 FINAL AGREEMENTS PROCESSING RESULTS:', {
      finalTodayDaysOff: todayDaysOff,
      finalTotalDaysOff: totalDaysOff,
      avgNetDaily,
      monthly,
      totalDuration
    });

    avgNetDaily = totalWeightedNetDaily / totalDuration;
    monthly = avgNetDaily * 30;
  }


  if (employeeToSeason.contract && startDate && endDate) {

    const selectedEndDate = selectedDate ?? new Date();

    endDate = new Date(endDate);
    const nextDayEndDate = new Date(endDate.getTime());
    nextDayEndDate.setDate(nextDayEndDate.getDate() + 1);
    const finalEndDate = +selectedEndDate < +nextDayEndDate ? selectedEndDate : endDate;

    eventCount = (await calendarEntriesApi.findEventsPerPeriodOfTime(
      startDate.toDateString(),
      addDays(finalEndDate, 1).toDateString(),
      employeeId,
      seasonId?.toString(),
    )).data as Record<string, number>;
    // const todayEventCount = (await calendarEntriesApi.findEventsPerPeriodOfTime(startDate.toUTCString(), (selectedDate ?? endDate).toUTCString(), employeeId.toString(), seasonId?.toString())).data as Record<string, number>;
    daysOff = eventCount[CalendarEntryEntityTypeEnum.DayOff] + eventCount[CalendarEntryType.HALF_DAY] * 0.5;

    if (customTypes) {
      customTypes.forEach(type => {
        if (type.countsAsDayOff && eventCount[type.name]) {
          daysOff += eventCount[type.name];
          daysOffBreakdown[type.name] = eventCount[type.name];
        }
      });
    }

    if (eventCount[CalendarEntryEntityTypeEnum.DayOff]) {
      daysOffBreakdown['Day Off'] = eventCount[CalendarEntryEntityTypeEnum.DayOff];
    }
    if (eventCount[CalendarEntryType.HALF_DAY]) {
      daysOffBreakdown['Half Day'] = eventCount[CalendarEntryType.HALF_DAY] * 0.5;
    }

    workDays = eventCount.WORK - (eventCount[CalendarEntryType.HALF_DAY] * 0.5);
  }

  if (agreements && startDate && endDate) {

    await computePaymentsData(startDate, endDate, employeeId, seasonId, agreements, payments, monthlyDues, monthlyPayments, credit, rewards, encryptionKey, customTypes);
    for (const payment of payments) {

      let alreadyPayed = 0;
      if (seasonId)
        // find previous payments, map to get a number array and reduce to get the total sum
        alreadyPayed = payments.filter(checkedPayment => checkedPayment.paymentDate <= payment.paymentDate && checkedPayment.id !== payment.id)
          .map(payment => encryptionKey && payment.paid_encrypted ? Number(decryptData(payment.paid_encrypted, encryptionKey)) : Number(payment.paid))
          .reduce((a, b) => {
            return a + b;
          }, 0);
      else {
        // if it is an annual employee compute only payments of current payment year
        const firstOfYear = new Date(Date.UTC(new Date(payment.paymentDate).getFullYear(), 0, 1));
        alreadyPayed = payments.filter(checkedPayment => checkedPayment.paymentDate <= payment.paymentDate && checkedPayment.id !== payment.id &&
          new Date(checkedPayment.paymentDate) > firstOfYear)
          .map(payment => encryptionKey && payment.paid_encrypted ? Number(decryptData(payment.paid_encrypted, encryptionKey)) : Number(payment.paid))
          .reduce((a, b) => {
            return a + b;
          }, 0);
      }
      const toBePayed = await computeDue(
        agreements,
        rewards,
        year ? new Date(new Date(payment.paymentDate).getFullYear(), 0, 1) : startDate,
        new Date(payment.paymentDate),
        employeeId,
        seasonId,
        encryptionKey,
        customTypes
      ) - alreadyPayed;

      const remainingCredit = toBePayed - (encryptionKey && payment.paid_encrypted ? Number(decryptData(payment.paid_encrypted, encryptionKey)) : Number(payment.paid));

      paymentsData.push({
        date: new Date(payment.paymentDate),
        payed: encryptionKey && payment.paid_encrypted ? Number(Number(decryptData(payment.paid_encrypted, encryptionKey)).toFixed(2)) : Number(Number(payment.paid).toFixed(2)),
        toBePayed: Number(toBePayed.toFixed(2)),
        remainingCredit: Number(remainingCredit.toFixed(2)),
        paymentId: payment.id,
        receiptPath: payment.filePath,
        isEncrypted: !!(payment.paid_encrypted && decryptData(payment.paid_encrypted, encryptionKey) === ''),
        paymentStaticType: payment.paymentStaticType
      });

    }

  }

  console.log('📊 FINAL REPORT VALUES:', {
    totalDaysOff,
    todayDaysOff,
    daysOff,
    workDays,
    agreementsCount: agreements.length
  });

  console.log('✅ FIX APPLIED: Negative fromStart values are now handled correctly - agreements that start after the selected date contribute 0 to todayDaysOff');

  // Fetch stats summary from the new endpoint
  let statsSummary: { eventCounts: Record<string, number>; workDays: number; totalDays: number } | undefined;
  try {
    // Use selectedDate as the end date if it is earlier than the contract end date
    // to reflect the "point of view" (POV) date.
    const currentSelectedDate = selectedDate ?? new Date();
    const statsEndDate = (currentSelectedDate < endDate) ? currentSelectedDate : endDate;

    const statsSummaryResponse = await calendarEntriesApi.getStatsSummary(
      format(startDate, 'yyyy-MM-dd'),
      format(statsEndDate, 'yyyy-MM-dd'),
      employeeId,
      seasonId?.toString()
    );
    statsSummary = statsSummaryResponse.data as any;
  } catch (error) {
    console.error('Failed to fetch stats summary:', error);
    statsSummary = { eventCounts: {}, workDays: 0, totalDays: 0 };
  }

  return {
    effectiveContractStartDate: contractStartDate,
    effectiveContractEndDate: endDate,

    contarctId: employeeToSeason.contract.id,

    contract: {
      startDate: startDate?.toLocaleDateString('it-IT'),
      endDate: endDate?.toLocaleDateString('it-IT'),
      days: workDays,
      // TODO to be defined on contract
      totalDaysOff,
      todayDaysOff,
      daysOff,
      daysOffBreakdown,
      legalContractType: employeeToSeason.contract.legalContractType
    },

    agreements: {
      avgNetDaily,
      monthly,
      total,
    },
    agreementsList: agreements,

    rewards,

    stats: {
      unpaidLeave: eventCount[CalendarEntryType.UNPAID_LEAVE],
      ill: eventCount[CalendarEntryType.ILL],
      vacation: eventCount[CalendarEntryType.VACATION],
      seniority,
    },

    statsSummary,

    chart: {
      paid: monthlyPayments,
      due: monthlyDues,
      credit,
    },

    paymentsData,

    paymentsCount: paymentsData.length,

  };

}

async function computePaymentsData(
  startDate: Date,
  endDate: Date,
  employeeId: number,
  seasonId: number | null,
  agreements: AgreementEntity[],
  payments: PaymentEntity[],
  monthlyDues: number[],
  monthlyPayments: number[],
  credit: number[],
  rewards: RewardEntity[],
  encryptionKey: string,
  customTypes: CustomCalendarEntryTypesEntity[]
) {
  for (let currentDate = startDate; currentDate <= endDate; currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)) {
    const firstDayOfMonth = startOfMonth(currentDate);
    const endDayOfMonth = endOfMonth(currentDate);

    let monthlyPayment = 0;

    monthlyDues.push(await computeDue(agreements, rewards, firstDayOfMonth, endDayOfMonth, employeeId, seasonId, encryptionKey, customTypes));

    payments.forEach((payment) => {
      if (isWithinInterval(new Date(payment.paymentDate), { start: firstDayOfMonth, end: endDayOfMonth })) {
        monthlyPayment = monthlyPayment + (encryptionKey && payment.paid_encrypted ? Number(decryptData(payment.paid_encrypted, encryptionKey)) : Number(payment.paid));
      }
    });

    monthlyPayments.push(monthlyPayment);


    if (credit.length === 0) {
      credit.push(monthlyDues[monthlyDues.length - 1] - monthlyPayment);
    } else {
      credit.push(monthlyDues[monthlyDues.length - 1] + credit[credit.length - 1] - monthlyPayment);
    }

  }
}

export async function computeDue(agreements: AgreementEntity[], rewards: RewardEntity[], startDate: Date, endDate: Date, employeeId: number, seasonId: number | null, encryptionKey: string, customTypes?: CustomCalendarEntryTypesEntity[]) {
  let monthlyDue = 0;
  for (const agreement of agreements) {


    let netValue = 0;

    if (agreement.net_daily_encrypted === null && agreement.net_daily) {
      netValue = agreement.net_daily;
    } else if (agreement.net_daily_encrypted !== null) {
      const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
      if (net !== '') {
        netValue = Number(net);
      } else
        continue;
    }
    let events: Record<string, number> | undefined;
    if (agreement.endedOn) {
      if (new Date(agreement.startedOn) >= startDate && new Date(agreement.endedOn) <= endDate) {
        events = (await calendarEntriesApi.findEventsPerPeriodOfTime(agreement.startedOn, agreement.endedOn, employeeId, seasonId?.toString())).data as Record<string, number>;
      }

      if (new Date(agreement.startedOn) < startDate && new Date(agreement.endedOn) <= endDate && new Date(agreement.endedOn) >= startDate) {
        events = (await calendarEntriesApi.findEventsPerPeriodOfTime(startDate.toDateString(), agreement.endedOn, employeeId, seasonId?.toString())).data as Record<string, number>;
      }

      if (new Date(agreement.startedOn) > startDate && new Date(agreement.endedOn) > endDate && new Date(agreement.startedOn) < endDate) {
        events = (await calendarEntriesApi.findEventsPerPeriodOfTime(agreement.startedOn, endDate.toDateString(), employeeId, seasonId?.toString())).data as Record<string, number>;
      }

      if (new Date(agreement.startedOn) <= startDate && new Date(agreement.endedOn) > endDate) {
        events = (await calendarEntriesApi.findEventsPerPeriodOfTime(startDate.toDateString(), endDate.toDateString(), employeeId, seasonId?.toString())).data as Record<string, number>;
      }
    } else if (new Date(agreement.startedOn) <= endDate) {

      if (new Date(agreement.startedOn) >= startDate)
        events = (await calendarEntriesApi.findEventsPerPeriodOfTime(agreement.startedOn, endDate.toDateString(), employeeId, seasonId?.toString())).data as Record<string, number>;

      if (new Date(agreement.startedOn) < startDate)
        events = (await calendarEntriesApi.findEventsPerPeriodOfTime(startDate.toDateString(), endDate.toDateString(), employeeId, seasonId?.toString())).data as Record<string, number>;

    }
    // if an agreement is valid in this month
    if (events) {
      monthlyDue += (events.WORK + events[CalendarEntryType.ILL] + events[CalendarEntryType.VACATION] + events[CalendarEntryType.DAY_OFF] + events[CalendarEntryType.HALF_DAY])
        * netValue;

      if (customTypes) {
        customTypes.forEach(type => {
          if (type.countsAsDayOff && events && events[type.name]) {
            monthlyDue += events[type.name] * netValue;
          }
        });
      }

      // After the main calculation, add all Special Daily Nets inside the date range to the total
      if (agreement.SpecialDailyNet && Array.isArray(agreement.SpecialDailyNet)) {
        agreement.SpecialDailyNet.forEach(sdn => {
          const sdnDate = new Date(sdn.date);
          if (sdnDate >= startDate && sdnDate <= endDate) {
            monthlyDue += sdn.net;
          }
        });
      }
    }

  }


  const monthlyRw = rewards
    .filter((rw) => {
      return (
        isAfter(new Date(rw.earnedOn), startDate) && isBefore(new Date(rw.earnedOn), endDate)) ||
        isSameDay(new Date(rw.earnedOn), endDate) ||
        isSameDay(new Date(rw.earnedOn), startDate)
    })
    .map((rw) => Number(rw.amount))
    .reduce((a, b) => { return a + b }, 0);

  monthlyDue = monthlyDue + monthlyRw;

  return monthlyDue;
}

export function getMonthlyNets(agreements: AgreementEntity[], date: Date, encryptionKey: string) {
  const results = [];

  for (const agr of agreements) {
    let netValue = 0;

    // if the agreement ended before the selected month, continue 
    if (agr.endedOn && isBefore(new Date(agr.endedOn), startOfMonth(date))) continue;
    // if the agreement will start after the selected month, continue 
    if (isAfter(new Date(agr.startedOn), endOfMonth(date))) continue;

    if (agr.net_daily_encrypted === null && agr.net_daily) {
      netValue = agr.net_daily;
    } else if (agr.net_daily_encrypted !== null) {
      const net = decryptData(agr.net_daily_encrypted, encryptionKey);
      if (net !== '') {
        netValue = Number(net);
      }
    }

    /* if (!isSameMonth(new Date(agr.startedOn), date) && (!agr.endedOn || !isSameMonth(new Date(agr.endedOn), date))) {
      result = result + (netValue * 31);
      continue;
    } else {
      result = result + (netValue * differenceInCalendarDays(
        agr.endedOn && isSameMonth(new Date(agr.endedOn), date) ? new Date(agr.endedOn) : endOfMonth(date),
        isSameMonth(new Date(agr.startedOn), date) ? new Date(agr.startedOn) : startOfMonth(date)
      ))
      if (!(agr.endedOn && isSameMonth(new Date(agr.endedOn), date))) {
        result = result + (netValue * (31 - endOfMonth(date).getDate()));
      }
    } */

    results.push(netValue * 30);
  }
  return results;
}

export interface CurrentPaymentDataType {
  totalEarned: number,
  totalPaid: number,
}
export function getDecryptedNetDaily(agreement: AgreementEntity, encryptionKey: string) {
  let netValue;
  if (agreement.net_daily_encrypted === null && agreement.net_daily) {
    netValue = agreement.net_daily;
  } else if (agreement.net_daily_encrypted !== null) {
    const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
    if (net !== '') {
      netValue = Number(net);
    }
  }
  return netValue ?? 0;
}
export function getDecryptedPayment(payment: PaymentEntity, encryptionKey: string): number {
  let netValue = 0;
  if (payment.paid_encrypted === null && payment.paid) {
    netValue = Number(payment.paid);
  } else if (payment.paid_encrypted !== null) {
    const net = decryptData(payment.paid_encrypted, encryptionKey);
    if (net !== '') {
      netValue = Number(net);
    }
  }
  return netValue ?? 0;
}

export async function fetchCurrentPaymentData(
  employeeId: number,
  seasonId: number | null,
  contractId: number | null,
  encryptionKey: string,
  date?: Date,
  contractStart?: Date | null,
  contractEnd?: Date | null
): Promise<CurrentPaymentDataType | undefined> {
  if (!employeeId)
    return;
  const agreements = (await (agreementApi.findAll(employeeId, seasonId ?? undefined, contractId ?? undefined))).data;
  // seasonId = 0 means null in this endpoint implementation
  let payments = (await (employeePaymentsApi.findAll(employeeId, seasonId ?? 0))).data;
  // For seasonal employees, exclude payments mis-tagged to this season at import
  // time by bounding them to the contract window (keeps "Versato" correct).
  if (seasonId) payments = filterPaymentsToContractWindow(payments, contractStart, contractEnd);
  const today = date ?? new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  let total = 0;
  for (const agreement of agreements) {
    let netValue = 0;

    netValue = getDecryptedNetDaily(agreement, encryptionKey);

    const computeStartedOn = () => {
      // if employee is seasonal
      if (seasonId)

        return new Date(agreement.startedOn);
      // if employee is annual
      else {

        const agreementStart = new Date(agreement.startedOn);
        return agreementStart > startOfYear ? agreementStart : startOfYear;
      }
    };


    // if agreements starts before current year start the count from first day of this year
    const startedOn = computeStartedOn();

    const computeEndedOn = () => {
      if (agreement.endedOn)
        return new Date(agreement.endedOn);
      else
        return new Date(today.getFullYear(), 11, 31);
    };

    const endedOn = computeEndedOn();

    startedOn.setHours(0, 0, 0, 0);
    endedOn.setHours(0, 0, 0, 0);


    // this checks that the agreement is valid in current year
    if (startedOn > endedOn)
      continue;

    if (endedOn) {
      if (endedOn <= today) {
        const events = (await calendarEntriesApi.findEventsPerPeriodOfTime(startedOn.toDateString(), endedOn.toDateString(), employeeId, seasonId?.toString())).data as Record<string, number>;
        // For every day that has a Special Daily Net, count it as a day of UNPAID_LEAVE, but do not double-count the event that was already present for that date
        if (agreement.SpecialDailyNet && Array.isArray(agreement.SpecialDailyNet)) {
          const actualEvents = (await calendarEntriesApi.findAll(employeeId.toString(), seasonId ?? 0)).data;

          agreement.SpecialDailyNet.forEach(async sdn => {
            const sdnDate = new Date(sdn.date.split("T")[0]);
            if (date && (isBefore(sdnDate, date) || isSameDay(sdnDate, date))) {
              const specialNetValue = getDecryptedSpecialDailyNet(sdn, encryptionKey);
              total += specialNetValue;
            }
            if (date && (isBefore(sdnDate, date) || isSameDay(sdnDate, date)) && sdnDate >= startedOn && sdnDate <= endedOn) {
              const event = actualEvents.find((event) => new Date(event.startedOn) <= sdnDate && new Date(event.endedOn) >= sdnDate)
              if (event) {
                events[event.type] = ((events[event.type] - 2) || 0);
              }
              else {
                events.WORK = (((events.WORK - 1) || 0));
              }
            }
          });
        }
        total += (events.WORK + events[CalendarEntryEntityTypeEnum.Ill] + events[CalendarEntryEntityTypeEnum.Vacation] + events[CalendarEntryEntityTypeEnum.DayOff]) *
          netValue + (events[CalendarEntryEntityTypeEnum.HalfDay] * netValue / 1);
      } else if (startedOn <= today) {
        const events = (await calendarEntriesApi.findEventsPerPeriodOfTime(startedOn.toDateString(), today.toDateString(), employeeId, seasonId?.toString())).data as Record<string, number>;
        // For every day that has a Special Daily Net, count it as a day of UNPAID_LEAVE, but do not double-count the event that was already present for that date              

        if (agreement.SpecialDailyNet) {
          const actualEvents = (await calendarEntriesApi.findAll(employeeId.toString(), seasonId ?? 0)).data;
          agreement.SpecialDailyNet.forEach(async sdn => {
            const sdnDate = new Date(sdn.date.split("T")[0]);
            if (date && (isBefore(sdnDate, date) || isSameDay(sdnDate, date))) {
              const specialNetValue = getDecryptedSpecialDailyNet(sdn, encryptionKey);
              total += specialNetValue;
            }
            if (date && (isBefore(sdnDate, date) || isSameDay(sdnDate, date)) && sdnDate >= startedOn && sdnDate <= endedOn) {
              const event = actualEvents.find((event) => new Date(event.startedOn) <= sdnDate && new Date(event.endedOn) >= sdnDate)
              if (event) {
                events[event.type] = ((events[event.type] - 2) || 0);
              }
              else {
                events.WORK = (((events.WORK - 1) || 0));
              }
            }
          });
        }

        total += (events.WORK + events[CalendarEntryEntityTypeEnum.Ill] + events[CalendarEntryEntityTypeEnum.Vacation] + events[CalendarEntryEntityTypeEnum.DayOff]) *
          netValue + (events[CalendarEntryEntityTypeEnum.HalfDay] * netValue / 1);


      }
    }
  }
  const totalEarned = Number(total.toFixed(2));
  let totalPaid = 0;
  if (seasonId) {

    totalPaid = payments
      .filter((payment) => new Date(payment.paymentDate) <= addDays(today, 1))
      .map(payment => (encryptionKey && payment.paid_encrypted ? Number(decryptData(payment.paid_encrypted, encryptionKey)) : Number(payment.paid))).reduce((a, b) => {
        return Number((a + b).toFixed(2));
      }, 0);
  }
  else {

    totalPaid = payments
      .filter((payment) => {
        const paymentDate = new Date(payment.paymentDate);
        return paymentDate >= startOfYear && paymentDate < addDays(today, 1);

      })
      .map(payment => (encryptionKey && payment.paid_encrypted ? Number(decryptData(payment.paid_encrypted, encryptionKey)) : Number(payment.paid))).reduce((a, b) => {
        return Number((a + b).toFixed(2));
      }, 0);
  }
  return {
    totalPaid,
    totalEarned
  }
}

export async function generateDailyNetEarnings(employeeId: number, rewards: RewardEntity[], seasonId: number, contractId: number, encryptionKey: string, startDate: string, endDate: string) {
  const agreements = (await agreementApi.findAll(employeeId, seasonId ?? undefined, contractId ?? undefined)).data;
  const dailyNetEarningsMap: Map<string, number> = new Map<string, number>();
  if (!agreements.length) {
    return dailyNetEarningsMap; // Return an empty map if there are no agreements
  }

  // Identify the earliest start date among the agreements
  const earliestStartDate = new Date(Math.min(...agreements.map(agreement => new Date(agreement.startedOn).getTime())));


  // Ensure the earliest start date is not earlier than the provided startDate
  let currentDate = new Date(Math.max(earliestStartDate.getTime(), new Date(startDate).getTime()));
  const endDateObj = parse(endDate, 'yyyy-MM-dd', new Date());
  // Initialize the map with dates
  // .setDate is not recognized by eslint - the condition is modified
  /* eslint-disable-next-line no-unmodified-loop-condition */
  while (currentDate <= endDateObj) {
    dailyNetEarningsMap.set(currentDate.toISOString().slice(0, 10), 0);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Reset the current date for iteration
  currentDate = new Date(Math.max(earliestStartDate.getTime(), new Date(startDate).getTime()));

  agreements.forEach(agreement => {
    const agreementStartDate = new Date(Date.UTC(
      parseInt(agreement.startedOn.substr(6, 4)),
      parseInt(agreement.startedOn.substr(3, 2)) - 1,
      parseInt(agreement.startedOn.substr(0, 2)),
    ));
    const agreementEndDate = agreement.endedOn ? parseISO(agreement.endedOn) : new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()));

    for (let date = new Date(Math.max(currentDate.getTime(), agreementStartDate.getTime()));
      // .setDate is not recognized by eslint - the condition is modified
      /* eslint-disable-next-line no-unmodified-loop-condition */
      date <= endDateObj && date <= agreementEndDate;
      date.setUTCDate(date.getUTCDate() + 1)) {  // Increment date in UTC

      const dateKey = date.toISOString().slice(0, 10);  // This is always in UTC
      let dailyNet = agreement.net_daily;

      if (agreement.net_daily_encrypted) {
        const decryptedValue = decryptData(agreement.net_daily_encrypted, encryptionKey);
        dailyNet = parseFloat(decryptedValue) || 0;
      }

      const rw = rewards.map((rw) => {
        return isSameDay(new Date(rw.earnedOn), date) ? Number(rw.amount) : 0
      }).reduce((a, b) => {
        return a + b;
      }, 0)

      dailyNetEarningsMap.set(dateKey, (dailyNetEarningsMap.get(dateKey) || 0) + (dailyNet || 0) + rw);
    }
  });

  // Calculate cumulative earnings
  let cumulativeTotal = 0;
  const dailyNetEarningsArray: Array<[string, number]> = Array.from(dailyNetEarningsMap.entries());
  dailyNetEarningsArray.forEach(([date, earnings], index) => {
    cumulativeTotal += earnings;
    dailyNetEarningsArray[index][1] = cumulativeTotal;
  });

  // Convert the array back to a map for the return value
  const cumulativeNetEarningsMap: Map<string, number> = new Map<string, number>(dailyNetEarningsArray);

  return cumulativeNetEarningsMap;
}

export async function generateCumulativePayouts(payments: PaymentDetail[], startDate: string, endDate: string): Promise<Map<string, number>> {
  const dateFormat = 'dd/MM/yyyy';
  const startDt = parse(startDate, dateFormat, new Date());
  const endDt = parse(endDate, dateFormat, new Date());
  const dailyPayoutsMap: Map<string, number> = new Map<string, number>();

  // Initialize the map with zeros for each date in the range
  for (let date = new Date(startDt); date <= endDt; date = addDays(date, 1)) {
    dailyPayoutsMap.set(format(date, 'yyyy-MM-dd'), 0);
  }

  // Populate the daily payouts map
  payments.forEach(payment => {

    const paymentDate = payment.date;
    const dateKey = format(paymentDate, 'yyyy-MM-dd'); // Format to YYYY-MM-DD for map keys

    if (paymentDate >= startDt && paymentDate <= endDt) {
      const currentAmount = dailyPayoutsMap.get(dateKey) || 0;
      dailyPayoutsMap.set(dateKey, currentAmount + parseFloat(payment.payed.toString()));
    }
  });

  // Calculate cumulative payouts
  let cumulativeTotal = 0;
  const cumulativePayoutsMap: Map<string, number> = new Map<string, number>();
  Array.from(dailyPayoutsMap.keys()).sort().forEach(dateKey => {
    cumulativeTotal += dailyPayoutsMap.get(dateKey) || 0;
    cumulativePayoutsMap.set(dateKey, cumulativeTotal);
  });

  return cumulativePayoutsMap;
}

export interface DailyPaymentData {
  date: Date;
  totalPaid: number;
  totalEarned: number;
}

export async function fetchPaymentDataForRange(
  employeeId: number,
  seasonId: number | null,
  contractId: number | null,
  encryptionKey: string,
  startDate: Date,
  endDate: Date
): Promise<DailyPaymentData[]> {
  if (!employeeId) return [];

  // Make a single API call to get all agreements
  const agreements = (await agreementApi.findAll(employeeId, seasonId ?? undefined, contractId ?? undefined)).data;
  const payments = (await employeePaymentsApi.findAll(employeeId, seasonId ?? 0)).data;
  const calendarEvents = (await calendarEntriesApi.findAll(employeeId.toString(), seasonId ?? 0)).data;

  const startOfYear = new Date(startDate.getFullYear(), 0, 1);
  const result: DailyPaymentData[] = [];

  // Find the earliest start date among agreements
  const earliestStartDate = new Date(Math.min(...agreements.map(agreement => new Date(agreement.startedOn).getTime())));

  // If seasonal, use agreement start date, if annual use start of year
  const calculationStartDate = seasonId ? earliestStartDate : startOfYear;

  // Process each day in the range
  for (let currentDate = new Date(startDate); currentDate <= endDate; currentDate = addDays(currentDate, 1)) {
    let totalEarned = 0;
    let totalPaid = 0;

    // Calculate total earned from start date up to current date (inclusive)
    for (let calculationDate = new Date(calculationStartDate); calculationDate <= addDays(currentDate, 1); calculationDate = addDays(calculationDate, 1)) {
      // Find all applicable agreements for this date
      const applicableAgreements = agreements.filter(agreement => {
        const agreementStart = new Date(agreement.startedOn);
        const agreementEnd = agreement.endedOn ? new Date(agreement.endedOn) : new Date(calculationDate.getFullYear(), 11, 31);

        // Adjust the date comparison to shift everything one day earlier
        const adjustedCalculationDate = addDays(calculationDate, 0);

        return (isBefore(adjustedCalculationDate, agreementEnd) || isSameDay(adjustedCalculationDate, agreementEnd)) &&
          (isAfter(adjustedCalculationDate, agreementStart) || isSameDay(adjustedCalculationDate, agreementStart));
      });

      // For each applicable agreement, calculate earnings
      for (const agreement of applicableAgreements) {
        const netValue = getDecryptedNetDaily(agreement, encryptionKey);

        // Check for special daily net
        const specialNet = agreement.SpecialDailyNet?.find(sdn =>
          isSameDay(new Date(sdn.date), calculationDate)
        );

        if (specialNet) {
          const specialNetValue = getDecryptedSpecialDailyNet(specialNet, encryptionKey);
          totalEarned += specialNetValue;
        } else {
          // Check for calendar events
          const event = calendarEvents.find(event =>
            (isBefore(new Date(event.startedOn), calculationDate) || isSameDay(new Date(event.startedOn), calculationDate)) &&
            (isAfter(new Date(event.endedOn), calculationDate) || isSameDay(new Date(event.endedOn), calculationDate))
          );

          if (!event || event.type !== CalendarEntryEntityTypeEnum.UnpaidLeave) {
            totalEarned += netValue;
          }
        }
      }
    }

    // Calculate total paid up to current date (inclusive)
    if (seasonId) {
      totalPaid = payments
        .filter(payment => {
          const paymentDate = new Date(payment.paymentDate);
          return paymentDate <= currentDate;
        })
        .reduce((sum, payment) => {
          const amount = encryptionKey && payment.paid_encrypted
            ? Number(decryptData(payment.paid_encrypted, encryptionKey))
            : Number(payment.paid);
          return Number((sum + amount).toFixed(2));
        }, 0);
    } else {
      totalPaid = payments
        .filter(payment => {
          const paymentDate = new Date(payment.paymentDate);
          return paymentDate >= startOfYear && paymentDate <= currentDate;
        })
        .reduce((sum, payment) => {
          const amount = encryptionKey && payment.paid_encrypted
            ? Number(decryptData(payment.paid_encrypted, encryptionKey))
            : Number(payment.paid);
          return Number((sum + amount).toFixed(2));
        }, 0);
    }

    result.push({
      date: new Date(currentDate),
      totalPaid,
      totalEarned: Number(totalEarned.toFixed(2))
    });
  }

  return result;
}
